// Pack desk: the order's notes, for the packer (per Gabe, 2026-10-30).
//
// Admins add notes on the order detail modal — instructions or issues with
// the order — and until now the packer never saw them. The desk fetches the
// order's thread as it opens the order and shows what a PERSON wrote (or the
// sales channel sent with the order). Portal's own system notes
// ("Consolidated into …", "moved to Pending Refund …") are left out: they
// are on thousands of orders a month and say nothing to the packer.
import { StickyNote } from "lucide-react";
import { apiFetch } from "../../api/apiFetch";
import type { OrderNote } from "./order-notes";

export type PackerNote = Pick<OrderNote, "note_id" | "body" | "created_by_uid" | "created_by_name" | "created_at">;

// A staff note carries the author's uid; a channel note (ingest) carries the
// channel code as its name. System writers are authorless and named
// "Portal" / "Portal repair" (or nothing at all on the oldest rows).
// A note with no letters in it is a reference CS logged for themselves —
// Poland adds carrier / booking numbers ("4121311399", "261028-073257") to
// ~40 orders a day — not something for the packer, so it doesn't pop up.
export const isPackerNote = (n: PackerNote): boolean => {
    if (!n.body || !/\p{L}/u.test(n.body)) return false;
    if (n.created_by_uid != null) return true;
    const name = (n.created_by_name || "").trim();
    return name !== "" && !/^portal\b/i.test(name);
};

// Oldest first — read top to bottom like the conversation it was.
// Never throws: a failed or slow fetch must not stop the order opening, so
// it resolves to no notes and the desk goes straight on.
export const fetchPackerNotes = async (orderId: string, timeoutMs = 4000): Promise<PackerNote[]> => {
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), timeoutMs);
    try {
        const r = await apiFetch(`/orders/${encodeURIComponent(orderId)}/notes`, { signal: ctrl.signal });
        if (!r.ok) return [];
        const data: { notes?: PackerNote[] } = await r.json();
        const notes = Array.isArray(data.notes) ? data.notes.filter(isPackerNote) : [];
        return notes.reverse();
    } catch {
        return [];
    } finally {
        window.clearTimeout(timer);
    }
};

const formatTs = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

export const PackerNotesList = ({ notes }: { notes: PackerNote[] }) => (
    <ul className="packer-notes" data-testid="packer-notes">
        {notes.map(n => (
            <li key={n.note_id} className="packer-note">
                <p className="packer-note-body">{n.body}</p>
                <div className="packer-note-meta">
                    <StickyNote size={12} />
                    {n.created_by_name && <span>{n.created_by_name}</span>}
                    <span>{formatTs(n.created_at)}</span>
                </div>
            </li>
        ))}
    </ul>
);
